import { RadialGauge } from 'canvas-gauges';
import { Mesh, MeshBasicMaterial, PlaneGeometry, Texture } from 'three';
import { ComponentOutput, SceneComponent } from '../SceneComponent';


const defaultInputs: Inputs = {
  contextHeight: 256,
  contextWidth: 256,
  threedWidth: 0.5,
  text: 'default',
  visible: true,
  textColor: "rgba(255,255,255,1.0)",
  updateInterval: 1000,
  signalTimeScale: 1,
  offsetY: 1,
};

type Inputs = {
  contextHeight: number;
  contextWidth: number;
  threedWidth: number;
  text: string;
  visible: boolean;
  textColor: string;
  updateInterval: number;
  signalTimeScale: number;
  offsetY: number;
}

type Outputs = {
  color: string;
  transparentColor: string;
  self: SceneComponent;
} & ComponentOutput;


class Gauge extends SceneComponent {
  private texture: Texture;
  private ctx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private mesh: Mesh<PlaneGeometry, MeshBasicMaterial>;
  private material: MeshBasicMaterial;
  private nextUpdate: number;
  private time: number;
  private x: number;
  private gauge: RadialGauge;
  private lastValue: number = 0;

  inputs: Inputs = {
    ...defaultInputs,
  }

  outputs = {
    color: 'rgb(80, 80, 80)',
    transparentColor: 'rgb(80, 80, 80, 0.1)',
    self: null,
  } as Outputs;

  constructor() {
    super();
    this.outputs.self = this;
  }

  onInit() {
    var THREE = this.context.three;
    var width = this.inputs.contextWidth;
    var height = this.inputs.contextHeight;
    var aspect = width / height;

    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    this.ctx = ctx;
    const texture1 = new THREE.CanvasTexture(this.ctx.canvas);
    texture1.needsUpdate = true;
    this.texture = texture1;

    console.log(this.ctx);

    this.updateContext();

    var material1 = new THREE.MeshBasicMaterial( {
      map: texture1, side:THREE.DoubleSide,
      // depthWrite: false,
    } );
    material1.transparent = true;
    material1.opacity = 1.0;
    var mesh1 = new THREE.Mesh(
      new THREE.PlaneGeometry(this.inputs.threedWidth, this.inputs.threedWidth / aspect),
      material1
    );
    this.mesh = mesh1;
    this.mesh.translateY(this.inputs.offsetY);
    mesh1.layers.set(31);
    this.material = material1;
    this.outputs.objectRoot = mesh1;
    this.nextUpdate = this.inputs.updateInterval;
    this.time = 0;
    this.x = 0;
  }

  onInputsUpdated() {
    this.mesh.visible = this.inputs.visible;
  }

  updateContext() {
    this.gauge = new RadialGauge({
      renderTo: this.canvas,
      width: 256,
      height: 256,
      units: "°C",
      title: "Temperature",
      minValue: 0,
      maxValue: 100,
      majorTicks: [
          0,
          10,
          20,
          30,
          40,
          50,
          60,
          70,
          80,
          90,
          100,
      ],
      minorTicks: 2,
      strokeTicks: true,
      highlights: [
          {
              "from": 0,
              "to": 30,
              "color": "rgba(0,255, 0, .3)"
          },
          {
              "from": 30,
              "to": 70,
              "color": "rgba(0, 0, 255, .7)"
          },
          {
            "from": 70,
            "to": 100,
            "color": "rgba(255, 0, 0, .3)"
        }
      ],
      ticksAngle: 225,
      startAngle: 67.5,
      colorMajorTicks: "#ddd",
      colorMinorTicks: "#ddd",
      colorTitle: "#eee",
      colorUnits: "#ccc",
      colorNumbers: "#eee",
      colorPlate: "#000",
      borderShadowWidth: 0,
      borders: true,
      needleType: "arrow",
      needleWidth: 2,
      needleCircleSize: 7,
      needleCircleOuter: true,
      needleCircleInner: false,
      animationRule: "linear",
      // animateOnInit: true,
      animationDuration: 1000,
      animation: true,
      colorBorderOuter: "#333",
      colorBorderOuterEnd: "#111",
      colorBorderMiddle: "#222",
      colorBorderMiddleEnd: "#111",
      colorBorderInner: "#111",
      colorBorderInnerEnd: "#333",
      colorNeedleShadowDown: "#333",
      colorNeedleCircleOuter: "#333",
      colorNeedleCircleOuterEnd: "#111",
      colorNeedleCircleInner: "#111",
      colorNeedleCircleInnerEnd: "#222",
      valueBoxBorderRadius: 0,
      colorValueBoxRect: "#00000000",
      colorValueBoxRectEnd: '#00000000',
      colorValueBoxBackground: '#00000000',
      colorValueText: '#eee',
      value: this.lastValue,
      valueDec: 0,
      valueInt: 2,
      fontValueSize: 64,
      valueBox: true,
  });
  this.gauge.draw();
  this.gauge.on('animate' as any, (value: number) => {
    this.texture.needsUpdate = true;
  })
  }

  onDestroy() {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  onTick(tickDelta: number) {
    this.time += tickDelta;
    if (this.time > this.nextUpdate) {
      this.nextUpdate += this.inputs.updateInterval;

      const amplitude = 40;
      const frequency = 0.01;
      const yNumeric = Math.trunc(amplitude * Math.sin(this.x/frequency) + amplitude);
      const y = yNumeric.toFixed(1);

      if (this.gauge) {
        this.gauge.options.value = this.lastValue;
        this.gauge.value = yNumeric;
        this.lastValue = yNumeric;
      }
      this.x += 0.01 * this.inputs.signalTimeScale;
      this.inputs.text = y;
      
      if (yNumeric> 70) {
        this.outputs.color = 'rgb(255, 0, 0)';
        this.outputs.transparentColor = 'rgba(255, 0, 0, 0.3)';
      }
      else if (yNumeric > 30) {
        this.outputs.color = 'rgb(0, 0, 255)';
        this.outputs.transparentColor = 'rgba(0, 0, 255, 0.2)';
      }
      else {
        this.outputs.color = 'rgb(0, 140, 0)';
        this.outputs.transparentColor = 'rgba(0, 140, 0, 0.1)';
      }

      this.updateContext();
      this.texture.needsUpdate = true;
    }
  }
}

export const gaugeType = 'mp.gauge';
export const makeGauge = function() {
  return new Gauge();
}
